import { BoutonVersionsRecu } from '../recus/RecuVersions';
import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Calendar, CalendarClock, Check, CheckCircle, CreditCard, Download, FileSignature, FileText, FolderArchive, Home, Landmark, Printer, Receipt, Wallet, ChevronDown, Building2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { formatDate, formatMoney, getPaymentMethod, CONTRIBUTION_STATUS } from './constants';

export default function PaiementsTab({
  archiveDoc,
  downloadDoc,
  expandedIds,
  filtered,
  generateRecuReference,
  getAllPayments,
  getAmount,
  getContributionStatus,
  getMode,
  getName,
  handleMarkAcquitted,
  handlePrintEcheancier,
  handlePrintRecuAcompte,
  handlePrintRecuContributionAcquitte,
  handlePrintRecuEcheance,
  isPaid,
  openEcheanceModal: openEcheanceModal,
  openPaymentModal,
  openEditPaymentDateModal,
  openSEPAModal,
  openScheduleModal,
  openStripeModal,
  processing,
  setPaymentForm,
  toggleExpand
}) {
  if (filtered.length === 0) {
    return (
      <div className="bg-white rounded-xl border p-12 text-center">
        <FileText className="h-12 w-12 mx-auto text-gray-300 mb-3" />
        <p className="text-gray-700 font-medium">Aucune souscription trouvee</p>
        <p className="text-sm text-gray-400">Les souscriptions apparaitront ici</p>
      </div>
    );
  }

  return (
    <div className="space-y-1" data-testid="paiements-list">
      {filtered.map((doc) => {
        const paid = isPaid(doc);
        const amount = getAmount(doc);
        const name = getName(doc);
        const method = getPaymentMethod(getMode(doc));
        const MethodIcon = method.icon;
        const contributionStatus = getContributionStatus(doc);
        const isSigned = doc.signature?.statut === 'signed' || contributionStatus === 'acquitted';
        const statusInfo = CONTRIBUTION_STATUS[contributionStatus];
        const StatusIcon = statusInfo.icon;
        const echeances = doc?.echeancier_personnalise?.echeances || [];
        const recuRef = doc.recu_contribution_ref || `RCS-${doc.reference}`;
        const isExpanded = expandedIds.has(doc.reference);

        const statusColor = contributionStatus === 'acquitted'
          ? 'border-green-300'
          : paid ? 'border-emerald-200' : 'border-amber-200';

        return (
          <div
            key={doc.reference}
            className={cn(
              "rounded-lg border transition-all duration-150",
              isExpanded ? `${statusColor} shadow-sm bg-white` : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm"
            )}
            data-testid={`paiement-row-${doc.reference}`}
          >
            {/* Compact row */}
            <button
              onClick={() => toggleExpand(doc.reference)}
              className="flex items-center gap-3 px-4 py-2.5 w-full text-left"
              data-testid={`paiement-toggle-${doc.reference}`}
            >
              <Home className={cn("h-4 w-4 shrink-0",
                contributionStatus === 'acquitted' ? 'text-green-600' : paid ? 'text-emerald-500' : 'text-amber-500'
              )} />
              <div className="min-w-0 flex-1">
                <p className={cn("font-medium text-sm truncate", isExpanded ? "text-blue-700" : "text-gray-900")}>{name}</p>
                <p className="text-[10px] text-gray-400 font-mono truncate">{doc.reference}</p>
              </div>
              <Badge className={cn("text-[10px] font-semibold border shrink-0", statusInfo.color)}>
                <StatusIcon className="h-3 w-3 mr-0.5" />{statusInfo.label}
              </Badge>
              <Badge variant="outline" className={cn("text-[10px] shrink-0 hidden sm:inline-flex", method.color)}>
                <MethodIcon className="h-3 w-3 mr-0.5" />{method.label}
              </Badge>
              <div className="hidden md:flex items-center gap-1 text-xs text-gray-500 shrink-0">
                <Building2 className="h-3 w-3" />
                <span className="font-mono">{doc.identite?.siret ? doc.identite.siret.slice(0, 9) + '...' : '-'}</span>
              </div>
              <span className={cn("text-sm font-bold tabular-nums shrink-0",
                contributionStatus === 'acquitted' ? 'text-green-700' : paid ? 'text-emerald-600' : 'text-amber-600'
              )}>{formatMoney(amount)} EUR</span>
              <ChevronDown className={cn("h-4 w-4 text-gray-400 shrink-0 transition-transform duration-200", isExpanded && "rotate-180 text-blue-500")} />
            </button>

            {/* Expanded panel */}
            {isExpanded && (
              <div className="px-4 pb-4 pt-1 border-t border-gray-100 animate-in fade-in slide-in-from-top-1 duration-150" data-testid={`paiement-details-${doc.reference}`}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Col 1: Info */}
                  <div className="space-y-2">
                    <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Souscripteur</h4>
                    <div className="space-y-1.5 text-sm">
                      <p className="font-medium text-gray-900">{name}</p>
                      <div className="text-gray-600 flex items-center gap-1.5">
                        <Building2 className="h-3.5 w-3.5 text-gray-400" />
                        SIRET: <span className="font-mono text-xs">{doc.identite?.siret || 'N/A'}</span>
                      </div>
                      <p className="text-gray-500 text-xs truncate">{doc.identite?.email || 'N/A'}</p>
                      <div className="p-2 bg-indigo-50 rounded-lg border border-indigo-100 mt-2">
                        <p className="text-[10px] text-indigo-600 font-medium">Recu contribution:</p>
                        <p className="font-mono text-xs text-indigo-800">{recuRef}</p>
                      </div>
                    </div>
                  </div>

                  {/* Col 2: Montant & Echeances */}
                  <div className="space-y-2">
                    <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Financier</h4>
                    <div className="space-y-1.5 text-sm">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Montant total:</span>
                        <span className="text-lg font-bold text-indigo-600">{formatMoney(amount)} EUR</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Prochaine echeance:</span>
                        <span className="text-gray-700">{formatDate(doc.prochaine_echeance?.date)}</span>
                      </div>
                    </div>
                    {echeances.length > 0 && (
                      <div className="bg-gray-50 rounded-lg p-2 border mt-2">
                        <p className="text-[10px] font-medium text-gray-600 mb-1.5">Echeances ({echeances.length})</p>
                        <div className="space-y-1 max-h-28 overflow-y-auto">
                          {echeances.map((ech, idx) => (
                            <div key={idx} className="flex items-center justify-between text-xs bg-white p-1.5 rounded gap-1">
                              <span className="text-gray-500">Ech. {idx + 1}</span>
                              <span className="font-mono text-[11px]">{formatMoney(ech.montant)}</span>
                              {ech.statut === 'paye' ? (
                                <div className="flex items-center gap-1">
                                  <Badge className="bg-green-700 text-white text-[10px] font-semibold">Paye</Badge>
                                  <Button size="sm" variant="ghost" className="h-5 px-1 text-[10px] text-indigo-600"
                                    onClick={(e) => { e.stopPropagation(); handlePrintRecuEcheance(doc, idx, generateRecuReference(doc, idx)); }}
                                    data-testid={`btn-recu-echeance-${doc.reference}-${idx}`}
                                  ><Receipt className="h-3 w-3" /></Button>
                                </div>
                              ) : (
                                <Button size="sm" variant="ghost" className="h-5 px-1.5 text-[10px] text-blue-600"
                                  onClick={(e) => { e.stopPropagation(); openPaymentModal(doc, idx); }}
                                >Payer</Button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {getAllPayments(doc).length > 0 && (
                      <div className="bg-amber-50 rounded-lg p-2 border border-amber-200 mt-2">
                        <p className="text-[10px] font-medium text-amber-800 mb-1.5 flex items-center gap-1">
                          <Wallet className="h-3 w-3" /> Acomptes ({getAllPayments(doc).length})
                        </p>
                        <div className="space-y-1 max-h-24 overflow-y-auto">
                          {getAllPayments(doc).map((acompte, idx) => (
                            <div key={idx} className="flex items-center justify-between text-xs bg-white p-1.5 rounded gap-1">
                              <span className="text-amber-700">AC{idx + 1}</span>
                              <span className="font-mono text-[11px]">{formatMoney(acompte.montant)}</span>
                              <span className="text-[10px] text-gray-500">{formatDate(acompte.date)}</span>
                              <Button size="sm" variant="ghost" className="h-5 px-1 text-[10px] text-amber-600"
                                onClick={(e) => { e.stopPropagation(); handlePrintRecuAcompte(doc, acompte, idx); }}
                                data-testid={`btn-recu-acompte-${doc.reference}-${idx}`}
                              ><Receipt className="h-3 w-3" /></Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Col 3: Actions */}
                  <div className="space-y-2">
                    <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Actions</h4>
                    <div className="space-y-1.5">
                      {/* Paiement en ligne */}
                      {!paid && (
                        <div className="flex gap-1.5">
                          <Button size="sm" onClick={(e) => { e.stopPropagation(); openStripeModal(doc); }}
                            className="h-8 text-xs gap-1 flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
                            data-testid={`paiement-stripe-${doc.reference}`}
                          ><CreditCard className="h-3.5 w-3.5" /> CB</Button>
                          <Button size="sm" onClick={(e) => { e.stopPropagation(); openSEPAModal(doc); }}
                            className="h-8 text-xs gap-1 flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                            data-testid={`paiement-sepa-${doc.reference}`}
                          ><Landmark className="h-3.5 w-3.5" /> SEPA</Button>
                        </div>
                      )}
                      {!paid && (
                        <div className="flex gap-1.5">
                          <Button size="sm" onClick={(e) => { e.stopPropagation(); setPaymentForm(prev => ({ ...prev, paymentType: 'acompte' })); openPaymentModal(doc); }}
                            className="h-8 text-xs gap-1 flex-1 bg-amber-500 hover:bg-amber-600 text-white"
                            data-testid={`paiement-acompte-${doc.reference}`}
                          ><Wallet className="h-3.5 w-3.5" /> Acompte</Button>
                          <Button size="sm" onClick={(e) => { e.stopPropagation(); setPaymentForm(prev => ({ ...prev, paymentType: 'total' })); openPaymentModal(doc); }}
                            className="h-8 text-xs gap-1 flex-1 bg-green-600 hover:bg-green-700 text-white"
                            data-testid={`paiement-total-${doc.reference}`}
                          ><CheckCircle className="h-3.5 w-3.5" /> Solde</Button>
                        </div>
                      )}
                      {paid && contributionStatus !== 'acquitted' && (
                        <Button size="sm" onClick={(e) => { e.stopPropagation(); handleMarkAcquitted(doc); }}
                          className="w-full h-8 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                          disabled={processing}
                        ><Check className="h-3.5 w-3.5" /> Marquer ACQUITTE</Button>
                      )}
                      <div className="flex flex-wrap gap-1.5">
                        <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); openScheduleModal(doc); }} className="h-7 text-[10px] gap-1">
                          <CalendarClock className="h-3 w-3" /> Echeancier
                        </Button>
                        <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handlePrintEcheancier(doc); }} className="h-7 text-[10px] gap-1">
                          <Printer className="h-3 w-3" /> Imprimer
                        </Button>
                        <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); downloadDoc(doc, 'attestation'); }}
                          className={cn("h-7 text-[10px] gap-1", isSigned ? 'bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600' : 'border-amber-300 text-amber-700')}
                          data-testid={isSigned ? `btn-attestation-signee-${doc.reference}` : `btn-attestation-draft-${doc.reference}`}
                        ><FileSignature className="h-3 w-3" /> {isSigned ? 'Attestation' : 'Draft'}</Button>
                      </div>
                      <div className="flex gap-1.5">
                        <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handlePrintRecuContributionAcquitte(doc); }}
                          className="h-7 text-[10px] gap-1 flex-1 border-indigo-200 text-indigo-700"
                        ><Receipt className="h-3 w-3" /> Recu {contributionStatus === 'acquitted' ? '(ACQUITTE)' : ''}</Button>
                        <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); downloadDoc(doc, 'recu'); }} disabled={!paid}
                          className="h-7 text-[10px] gap-1 border-emerald-200 text-emerald-700"
                        ><Download className="h-3 w-3" /> Recu ESS</Button>
                        <BoutonVersionsRecu produit="domium" ident={doc.reference} titre={doc.reference} className="h-7 text-[10px]" testId={`versions-recu-domium-${doc.reference}`} />
                        <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); archiveDoc(doc); }}
                          className="h-7 text-[10px] gap-1"
                        ><FolderArchive className="h-3 w-3" /> GED</Button>
                      </div>
                      {/* V37.38 — Bouton de rectification de la date de paiement (Super Admin) */}
                      {paid && openEditPaymentDateModal && (
                        <div className="flex gap-1.5 mt-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); openEditPaymentDateModal(doc); }}
                            className="h-7 text-[10px] gap-1 flex-1 border-amber-300 text-amber-700 hover:bg-amber-50"
                            data-testid={`btn-edit-payment-date-${doc.reference}`}
                            title="Rectifier la date de paiement pour mise a jour comptable"
                          >
                            <Calendar className="h-3 w-3" /> Modifier date paiement
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
